import { valibotResolver } from "@hookform/resolvers/valibot";
import { PencilSimpleLine } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import Skeleton from "common/components/ui/Skeleton";

import useAgencySettings from "../../datahooks/useAgencySettings";
import useAgencySettingsMutations from "../../datahooks/useAgencySettingsMutations";
import { AgencyNameSchema, type AgencyNameSchemaType } from "../../schema";
import SettingsItemContainer from "../SettingsItemContainer";

export default function Name() {
  const [isEditing, setIsEditing] = useState(false);
  const { agencySettings, isLoadingAgencySettings } = useAgencySettings();
  const { updateAgencySettings, isUpdatingAgencySettings } =
    useAgencySettingsMutations();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { name: agencySettings ? agencySettings.name : "" },
    resolver: valibotResolver(AgencyNameSchema),
  });

  useEffect(() => {
    if (agencySettings) {
      reset({ name: agencySettings.name });
    }
  }, [agencySettings]);

  function onSubmit({ name }: AgencyNameSchemaType) {
    updateAgencySettings({ updates: { name } }).then(() => {
      setIsEditing(false);
    });
  }
  function onCancel() {
    setIsEditing(false);
    reset({ name: agencySettings.name });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SettingsItemContainer className="items-center">
        {isEditing ? (
          <FormField
            label="Agency name"
            htmlFor="agency-name"
            error={errors.name?.message}
            size="sm"
          >
            <div className="gap-x-2 flex">
              <Input
                id="agency-name"
                placeholder="Enter name"
                variant="sm"
                className="w-80"
                {...register("name")}
              />
              <div className="flex gap-x-2 mt-auto ml-auto">
                <Button
                  variant="secondary-black"
                  size="sm"
                  onClick={onCancel}
                  disabled={isUpdatingAgencySettings}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  type="submit"
                  variant="secondary-purple"
                  isLoading={isUpdatingAgencySettings}
                >
                  Save
                </Button>
              </div>
            </div>
          </FormField>
        ) : (
          <div className="flex flex-col gap-y-0.5">
            <span className="text-sm-bold">Agency name</span>
            {isLoadingAgencySettings ? (
              <Skeleton />
            ) : (
              <span className="text-black-500 text-xs-medium">
                {agencySettings.name}
              </span>
            )}
          </div>
        )}
        {!isEditing && (
          <Button
            variant="ghost-black"
            intent="iconOnly"
            className="ml-auto"
            onClick={() => setIsEditing(true)}
          >
            <PencilSimpleLine />
          </Button>
        )}
      </SettingsItemContainer>
    </form>
  );
}
